import React, { createContext, useContext, useEffect, useState } from 'react';
import { checkLocationRestriction } from '../utils/locationUtils';
import LocationResctrictionBarrierScreen from '../components/LocationResctrictionBarrierScreen';

interface LocationContextType {
  isRestricted: boolean;
  countryCode: string | null;
  countryName: string | null;
  city: string | null;
  loading: boolean;
  error: string | null;
}

const initialContextState: LocationContextType = {
  isRestricted: false,
  countryCode: null,
  countryName: null,
  city: null,
  loading: true,
  error: null,
};

const LocationContext = createContext<LocationContextType>(initialContextState);

export const useLocation = () => useContext(LocationContext);

export const LocationProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [locationState, setLocationState] = useState<LocationContextType>(initialContextState);

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const { isRestricted, location } = await checkLocationRestriction();
        setLocationState({
          isRestricted,
          countryCode: location.countryCode,
          countryName: location.countryName,
          city: location.city,
          loading: false,
          error: location.error || null,
        });
      } catch (error) {
        setLocationState({
          ...initialContextState,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to check location restrictions',
        });
      }
    };

    fetchLocationData();
  }, []);

  return (
    <LocationContext.Provider value={locationState}>
      {children}
    </LocationContext.Provider>
  );
};

export const LocationRestrictionBarrier: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { isRestricted, countryName } = useLocation();
  
  if (isRestricted) {
    return (
      <LocationResctrictionBarrierScreen 
        countryName={countryName}
      />
    );
  }
  
  return <>{children}</>;
};

export default LocationContext; 